import { useState, useMemo } from "react";
import { useData } from "@/context/data-provider";
import { calcScore } from "@/lib/scoring";
import { todayKey } from "@/lib/constants";
import { downloadCsv, toCsv } from "@/lib/csv";

export default function Plano() {
  const {
    doctors: { doctors },
    visits: { visits },
    period: { period },
    plans: { plans, addPlan, togglePlan, removePlan },
  } = useData();

  const [showAdd, setShowAdd] = useState(false);
  const [searchDoc, setSearchDoc] = useState("");
  const [acOpen, setAcOpen] = useState(false);
  const [selDocId, setSelDocId] = useState<string | null>(null);
  const [planTime, setPlanTime] = useState("");
  const [planNote, setPlanNote] = useState("");

  const doneCount = plans.filter((p) => p.done).length;

  const acResults = useMemo(() => {
    if (!searchDoc.trim()) return [];
    const q = searchDoc.toLowerCase();
    const plannedIds = new Set(plans.map((p) => p.doctorId));
    return doctors
      .filter((d) => !plannedIds.has(d.id) && (d.name.toLowerCase().includes(q) || d.local.toLowerCase().includes(q)))
      .slice(0, 6);
  }, [searchDoc, doctors, plans]);

  const suggestions = useMemo(() => {
    const plannedIds = new Set(plans.map((p) => p.doctorId));
    return doctors
      .filter((d) => !plannedIds.has(d.id))
      .map((d) => ({ ...d, score: calcScore(d, visits, period) }))
      .sort((a, b) => a.score - b.score)
      .slice(0, 5);
  }, [doctors, plans, visits, period]);

  const handleAdd = () => {
    const doc = doctors.find((d) => d.id === selDocId);
    if (!doc) return;
    addPlan({ doctorId: doc.id, name: doc.name, time: planTime, note: planNote, done: false, local: doc.local });
    setSearchDoc("");
    setSelDocId(null);
    setPlanTime("");
    setPlanNote("");
    setShowAdd(false);
  };

  const exportCsv = () => {
    const headers = ["Médico", "Local", "Hora", "Objetivo", "Concluído"];
    const rows = plans.map((p) => [p.name, p.local, p.time, p.note, p.done ? "Sim" : "Não"]);
    downloadCsv(`plano_${todayKey()}.csv`, toCsv(headers, rows));
  };

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-serif text-[1.3rem]" style={{ color: "var(--mv-ink-dark)" }}>Plano Diário</h1>
          <span className="font-mono text-[0.68rem]" style={{ color: "var(--mv-ink-light)" }}>
            {doneCount}/{plans.length} concluídos
          </span>
        </div>
        <div className="flex gap-2">
          {plans.length > 0 && (
            <button className="mv-btn mv-btn-ghost mv-btn-sm" onClick={exportCsv}>📥 CSV</button>
          )}
          <button className="mv-btn mv-btn-primary mv-btn-sm" onClick={() => setShowAdd(!showAdd)}>+ Adicionar</button>
        </div>
      </div>

      {/* Progress bar */}
      {plans.length > 0 && (
        <div className="h-[4px] rounded-sm overflow-hidden mb-4" style={{ background: "var(--mv-surface2)" }}>
          <div
            className="h-full rounded-sm transition-all duration-500"
            style={{
              width: `${(doneCount / plans.length) * 100}%`,
              background: "linear-gradient(90deg,#4A9E2F,#5BAD3E)",
            }}
          />
        </div>
      )}

      {/* Add form */}
      {showAdd && (
        <div className="mv-panel mb-4">
          <div className="mv-panel-header">
            <span className="mv-panel-title">Adicionar ao Plano</span>
          </div>
          <div className="p-[14px_18px]">
            <div className="relative mb-[9px]">
              <input
                className="mv-input"
                placeholder="Pesquisar médico..."
                value={searchDoc}
                onChange={(e) => { setSearchDoc(e.target.value); setSelDocId(null); setAcOpen(true); }}
                onFocus={() => setAcOpen(true)}
                onBlur={() => setTimeout(() => setAcOpen(false), 200)}
              />
              {acOpen && acResults.length > 0 && (
                <div
                  className="absolute top-[calc(100%+3px)] left-0 right-0 z-[200] rounded-[9px] max-h-[200px] overflow-y-auto"
                  style={{ background: "var(--mv-surface)", border: "1.5px solid var(--mv-blue)", boxShadow: "0 8px 24px var(--mv-shadow)" }}
                >
                  {acResults.map((d) => (
                    <div
                      key={d.id}
                      className="p-[9px_12px] cursor-pointer flex items-center justify-between gap-2 transition-colors hover:bg-[var(--mv-blue-light)]"
                      style={{ borderBottom: "1px solid var(--mv-border)" }}
                      onMouseDown={() => { setSelDocId(d.id); setSearchDoc(d.name); setAcOpen(false); }}
                    >
                      <div>
                        <div className="font-semibold text-[0.82rem]">{d.name}</div>
                        <div className="text-[0.67rem]" style={{ color: "var(--mv-ink-light)" }}>{d.local}</div>
                      </div>
                      <span className={`ptag ptag-${d.prio}`}>{d.prio}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="mb-[9px]">
              <label className="mv-label">Objetivo</label>
              <input className="mv-input" value={planNote} onChange={(e) => setPlanNote(e.target.value)} placeholder="Objetivo..." />
            </div>
            <div className="mb-[9px]" style={{ maxWidth: 140 }}>
              <label className="mv-label">Hora</label>
              <input type="time" className="mv-input" value={planTime} onChange={(e) => setPlanTime(e.target.value)} />
            </div>
            <div className="flex gap-2 justify-end">
              <button className="mv-btn mv-btn-ghost mv-btn-sm" onClick={() => setShowAdd(false)}>Cancelar</button>
              <button className="mv-btn mv-btn-primary mv-btn-sm" onClick={handleAdd} disabled={!selDocId}>Adicionar</button>
            </div>
          </div>
        </div>
      )}

      {/* Plan list */}
      <div className="mv-panel mb-4">
        <div className="mv-panel-header">
          <span className="mv-panel-title">Médicos Planeados</span>
          <span className="mv-badge">{plans.length}</span>
        </div>
        {plans.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📝</div>
            Nenhuma visita planeada para hoje.
          </div>
        ) : (
          <div>
            {plans.map((p) => (
              <div key={p.doctorId} className={`plan-item ${p.done ? "completed" : ""}`}>
                <button
                  className={`plan-check ${p.done ? "checked" : ""}`}
                  onClick={() => togglePlan(p.doctorId)}
                >
                  {p.done ? "✓" : ""}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-[0.82rem] font-semibold" style={{ color: "var(--mv-ink-dark)", textDecoration: p.done ? "line-through" : undefined }}>{p.name}</div>
                  <div className="text-[0.68rem]" style={{ color: "var(--mv-ink-light)" }}>{p.local}</div>
                  {p.note && <div className="text-[0.68rem] mt-[2px]" style={{ color: "var(--mv-ink-dark)" }}>{p.note}</div>}
                </div>
                {p.time && (
                  <span className="font-mono text-[0.6rem]" style={{ color: "var(--mv-orange)" }}>⏰ {p.time}</span>
                )}
                <button
                  className="mv-btn mv-btn-ghost mv-btn-sm"
                  style={{ padding: "2px 6px", fontSize: "0.9rem" }}
                  onClick={() => removePlan(p.doctorId)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="mv-panel">
          <div className="mv-panel-header">
            <span className="mv-panel-title">💡 Sugestões</span>
          </div>
          <div>
            {suggestions.map((d) => (
              <div key={d.id} className="doctor-item">
                <div className={`prio-dot prio-dot-${d.prio}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[0.81rem]" style={{ color: "var(--mv-ink-dark)" }}>{d.name}</div>
                  <div className="text-[0.67rem]" style={{ color: "var(--mv-ink-light)" }}>{d.local}</div>
                </div>
                <span className="font-mono text-[0.6rem]" style={{ color: "var(--mv-ink-light)" }}>Score: {d.score}</span>
                <button
                  className="mv-btn mv-btn-primary mv-btn-sm"
                  onClick={() => addPlan({ doctorId: d.id, name: d.name, time: "", note: "", done: false, local: d.local })}
                >
                  +
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
